import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db, storage } from '../../../firebase/config';
import {
  collection, query, orderBy, onSnapshot, deleteDoc, doc,
  addDoc, serverTimestamp, updateDoc, getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Box, Typography, Button, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, Collapse, Dialog, DialogContent,
  DialogTitle, DialogActions, LinearProgress,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useQRLogo } from '../../../hooks/useQRLogo';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LinkIcon from '@mui/icons-material/Link';
import TvIcon from '@mui/icons-material/Tv';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PollIcon from '@mui/icons-material/Poll';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DownloadIcon from '@mui/icons-material/Download';
import WorkshopPhotoCarousel from '../components/WorkshopPhotoCarousel';

import { generateSessionCode } from '../utils/sessionCode';

const accent = '#E05500';

// ── WorkshopN logotyp med accent-N ───────────────────────────────────────────
export function WorkshopNLogo({ variant = 'h5' }) {
  return (
    <Typography variant={variant} fontWeight={700} component="span">
      Workshop<span style={{ color: accent }}>N</span>
    </Typography>
  );
}

// ── Instruktionspanel ─────────────────────────────────────────────────────────

function Step({ number, title, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%', bgcolor: accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, mt: 0.2,
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>{number}</Typography>
      </Box>
      <Box>
        <Typography fontWeight={700} mb={0.4}>{title}</Typography>
        <Typography color="text.secondary" fontSize="0.85rem" sx={{ lineHeight: 1.6 }}>{children}</Typography>
      </Box>
    </Box>
  );
}

function InstructionsPanel() {
  const [open, setOpen] = useState(false);
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2, mb: 3, overflow: 'hidden',
        borderColor: open ? accent + '66' : accent + '44',
        background: open
          ? `linear-gradient(135deg, ${accent}0a 0%, transparent 60%)`
          : `linear-gradient(135deg, ${accent}08 0%, transparent 50%)`,
        transition: 'border-color 0.2s, background 0.3s',
      }}
    >
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: accent + '0a' },
          transition: 'background 0.15s',
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          bgcolor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.95rem', lineHeight: 1 }}>?</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} color={accent} fontSize="0.95rem">Så här riggar du en workshop</Typography>
          {!open && (
            <Typography fontSize="0.75rem" color="text.secondary">
              Steg-för-steg guide · Hur deltagare ansluter · QR, länk och hybridläge
            </Typography>
          )}
        </Box>
        {open ? <ExpandLessIcon sx={{ color: accent }} /> : <ExpandMoreIcon sx={{ color: accent }} />}
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          <Box>
            <Typography color={accent} fontWeight={700} fontSize="0.75rem" letterSpacing={1} mb={2}>STEG FÖR STEG</Typography>
            <Step number="1" title="Skapa workshopen">Klicka "Ny workshop". Välj mall eller bygg från scratch. Sätt titel, datum, läge (digital/hybrid), grupper och frågor (fritext, skala, flerval eller ordmoln). Ladda upp en omslagsbild för att sätta stämningen.</Step>
            <Step number="2" title="Bjud in deltagare">
              Använd "Bjud in deltagare" i editorn för att skicka e-post direkt, eller dela länken <code style={{ color: accent, background: 'rgba(224,85,0,0.1)', padding: '1px 6px', borderRadius: 4 }}>/workshop/[sessionskod]</code> via Teams eller som QR-kod på storskärm.
            </Step>
            <Step number="3" title="Starta sessionen">Öppna presentationsläget (▶) och aktivera sessionen. Svar börjar komma in direkt i realtid per grupp.</Step>
            <Step number="4" title="Presentera resultaten">Navigera mellan frågorna med piltangenterna. Ordmolnsfrågor växer fram interaktivt. Skala-frågor visas som siffror per grupp.</Step>
            <Step number="5" title="AI-analys">Klicka "Analysera med AI" längst ner i presentationsläget för att få en konklusion, teman, styrkor, utmaningar och förslag på nästa steg.</Step>
            <Step number="6" title="Lås av">Klicka på låset för att stänga för nya svar. Deltagarna kan fortfarande se att sessionen är klar.</Step>
          </Box>
          <Box>
            <Typography color={accent} fontWeight={700} fontSize="0.75rem" letterSpacing={1} mb={2}>HUR DELTAGARE ANSLUTER</Typography>
            {[
              { icon: <QrCodeIcon sx={{ fontSize: 18, color: accent }} />, title: 'QR-kod på storskärm', badge: 'Rekommenderat', text: 'Visa QR-koden på projektorn. Deltagarna skannar med mobilen och loggar in med Microsoft-kontot — tar ca 5 sekunder.' },
              { icon: <LinkIcon sx={{ fontSize: 18, color: '#185FA5' }} />, title: 'Länk via Teams / mail', text: 'Skicka deltagarlinken i Teams-chatten eller som kalenderinbjudan.' },
              { icon: <PrintIcon sx={{ fontSize: 18, color: '#0F6E56' }} />, title: 'Hybridblad (papper + AI)', badge: 'Hybrid', text: 'Välj hybridläge och skriv ut svarblad (🖨️-knappen i listan). Varje blad är kopplat till en specifik fråga och grupp via QR-kod. Deltagarna skriver på pappret. Fotografera sedan bladet och scanna QR-koden — AI läser av svaren och sparar dem automatiskt.' },
              { icon: <TvIcon sx={{ fontSize: 18, color: '#854F0B' }} />, title: 'Distansdeltagare via Teams', text: 'Dela deltagarlinken i Teams-mötet. Distansdeltagare öppnar länken parallellt.' },
            ].map((item, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {item.icon}
                  <Typography fontWeight={700} fontSize="0.9rem">{item.title}</Typography>
                  {item.badge && <Chip label={item.badge} size="small" sx={{ bgcolor: accent + '22', color: accent, fontSize: '0.65rem', height: 18 }} />}
                </Box>
                <Typography color="text.secondary" fontSize="0.82rem" sx={{ lineHeight: 1.5 }}>{item.text}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
            <Typography color="text.secondary" fontSize="0.78rem" sx={{ lineHeight: 1.5 }}>
              <strong>Inloggning:</strong> Alla deltagare loggar in med sitt LagbasN-konto. Svaren kopplas automatiskt till rätt person och affärsområde.
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── QR-dialog ─────────────────────────────────────────────────────────────────

function QRDialog({ url, title, open, onClose }) {
  const qrLogo = useQRLogo();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
        <Typography fontWeight={700} textAlign="center">{title}</Typography>
        <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2 }}>
          <QRCodeSVG
            value={url} size={220}
            level="H"
            {...(qrLogo ? {
              imageSettings: { src: qrLogo, height: 48, width: 48, excavate: true },
            } : {})}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
          {url}
        </Typography>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', mt: 1 }}>Stäng</Button>
      </DialogContent>
    </Dialog>
  );
}

function InviteQRDialog({ open, onClose, workshopId, workshopTitle }) {
  const qrLogo = useQRLogo();
  const svgWrapRef = React.useRef();
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/workshopn/join/${workshopId}`;

  const handleDownload = () => {
    const svgEl = svgWrapRef.current?.querySelector('svg');
    if (!svgEl) return;
    // Add white background rect before serializing
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', 'white');
    clone.insertBefore(bg, clone.firstChild);
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `qr-anmalan-${(workshopTitle || workshopId).replace(/[^a-zA-Z0-9åäöÅÄÖ\-_]/g, '_')}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>Bjud in via QR</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
        <Typography fontSize="0.85rem" color="text.secondary" textAlign="center">
          Dela QR-koden i ett mail, kalenderinbjudan eller Teams-chatt. Deltagaren skannar och anmäler sig med namn och e-post.
        </Typography>

        {/* Display + download source */}
        <Box ref={svgWrapRef} sx={{ bgcolor: '#fff', p: 2.5, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
          <QRCodeSVG
            value={url}
            size={220}
            level="H"
            {...(qrLogo ? {
              imageSettings: { src: qrLogo, height: 40, width: 40, excavate: true },
            } : {})}
          />
        </Box>

        <Typography variant="caption" color="text.secondary"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center', fontSize: '0.7rem' }}>
          {url}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            variant="outlined" size="small" fullWidth startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{ textTransform: 'none', borderColor: copied ? '#4ade80' : 'divider', color: copied ? '#4ade80' : 'text.secondary' }}
          >
            {copied ? 'Kopierat!' : 'Kopiera länk'}
          </Button>
          <Button
            variant="contained" size="small" fullWidth startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
          >
            Ladda ner SVG
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Adminlista ────────────────────────────────────────────────────────────────

export default function WorkshopNAdmin() {
  const navigate = useNavigate();
  useAuth();
  const [sessions, setSessions]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [copied, setCopied]           = useState(null);
  const [duplicated, setDuplicated]   = useState(null);
  const [qrSession, setQrSession]     = useState(null);
  const [sortBy, setSortBy]           = useState('createdAt');
  const [sortDir, setSortDir]         = useState('desc');
  const [filterAO, setFilterAO]       = useState('');
  const [filterFacil, setFilterFacil] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [photoDialog, setPhotoDialog] = useState(null); // { id, title, photos }
  const [inviteDialog, setInviteDialog] = useState(null); // { id, title }
  const [photoUploading, setPhotoUploading] = useState(false);

  const handlePhotoUpload = async (workshopId, files) => {
    setPhotoUploading(true);
    // Read current photos from Firestore to avoid stale closure when uploading multiple files
    const wsSnap = await getDoc(doc(db, 'workshops', workshopId));
    let currentPhotos = wsSnap.exists() ? (wsSnap.data().photos || []) : [];
    for (const file of files) {
      try {
        const ext = file.name.split('.').pop() || 'jpg';
        const photoId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const storageRef = ref(storage, `workshops/${workshopId}/photos/${photoId}.${ext}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        const newPhoto = { id: photoId, url, name: file.name, uploadedAt: new Date().toISOString() };
        currentPhotos = [...currentPhotos, newPhoto];
        const photosSnapshot = currentPhotos;
        await updateDoc(doc(db, 'workshops', workshopId), { photos: photosSnapshot });
        setPhotoDialog(prev => prev ? { ...prev, photos: photosSnapshot } : prev);
      } catch (e) {
        console.error('Photo upload failed:', e);
      }
    }
    setPhotoUploading(false);
  };

  const handlePhotoDelete = async (workshopId, photo) => {
    try {
      const storageRef = ref(storage, `workshops/${workshopId}/photos/${photo.id}.${photo.name.split('.').pop() || 'jpg'}`);
      await deleteObject(storageRef).catch(() => {});
      const updatedPhotos = (photoDialog?.photos || []).filter(p => p.id !== photo.id);
      await updateDoc(doc(db, 'workshops', workshopId), { photos: updatedPhotos });
      setPhotoDialog(prev => prev ? { ...prev, photos: updatedPhotos } : prev);
    } catch (e) {
      console.error('Photo delete failed:', e);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'workshops'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  const allAOs   = [...new Set(sessions.map(s => s.departmentName).filter(Boolean))].sort();
  const allFacils = [...new Set(sessions.map(s => s.createdByName).filter(Boolean))].sort();

  const visibleSessions = sessions
    .filter(s => {
      if (filterAO && s.departmentName !== filterAO) return false;
      if (filterFacil && s.createdByName !== filterFacil) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        return (
          s.title?.toLowerCase().includes(q) ||
          s.createdByName?.toLowerCase().includes(q) ||
          s.departmentName?.toLowerCase().includes(q) ||
          s.sessionCode?.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'title':       av = a.title || ''; bv = b.title || ''; break;
        case 'date':        av = a.date || ''; bv = b.date || ''; break;
        case 'facilitator': av = a.createdByName || ''; bv = b.createdByName || ''; break;
        case 'ao':          av = a.departmentName || ''; bv = b.departmentName || ''; break;
        default:            av = a.createdAt?.seconds || 0; bv = b.createdAt?.seconds || 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const SortBtn = ({ field, label }) => (
    <Button size="small" onClick={() => toggleSort(field)}
      endIcon={sortBy === field ? (sortDir === 'asc' ? '↑' : '↓') : null}
      sx={{
        textTransform: 'none', fontSize: '0.78rem',
        color: sortBy === field ? accent : 'text.secondary',
        bgcolor: sortBy === field ? accent + '15' : 'transparent',
        '&:hover': { bgcolor: accent + '15' },
        px: 1.5, py: 0.5, minWidth: 0, borderRadius: 1,
      }}
    >
      {label}
    </Button>
  );

  const handleDuplicate = async (e, s) => {
    e.stopPropagation();
    const newCode = generateSessionCode();
    const newDoc = await addDoc(collection(db, 'workshops'), {
      title:          `${s.title} (kopia)`,
      config:         s.config         || {},
      imageUrl:       s.imageUrl       || null,
      departmentName: s.departmentName || null,
      createdByName:  s.createdByName  || null,
      mode:           s.mode           || 'digital',
      sessionCode:    newCode,
      status:         { active: false, locked: false, currentQuestion: 0 },
      createdAt:      serverTimestamp(),
      updatedAt:      serverTimestamp(),
    });
    setDuplicated(s.id);
    setTimeout(() => setDuplicated(null), 2500);
    navigate(`/workshopn/${newDoc.id}/edit`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Ta bort workshopen?')) return;
    await deleteDoc(doc(db, 'workshops', id));
  };

  const copyCode = (e, sessionCode) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/workshop/${sessionCode}`);
    setCopied(sessionCode);
    setTimeout(() => setCopied(null), 2000);
  };

  const statusLabel = (s) => {
    if (s?.locked) return { label: 'Låst', color: '#999' };
    if (s?.active) return { label: 'Aktiv', color: '#4ade80' };
    return { label: 'Utkast', color: accent };
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <WorkshopNLogo variant="h5" />
        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/workshopn/new')}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
        >
          Ny workshop
        </Button>
      </Box>

      <InstructionsPanel />

      {/* Filter + sök */}
      {!loading && sessions.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box
              component="input"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              placeholder="Sök..."
              sx={{
                bgcolor: 'background.paper', border: '1px solid',
                borderColor: 'divider', borderRadius: 1,
                color: 'text.primary', px: 1.5, py: 0.6, fontSize: '0.82rem',
                outline: 'none', width: 160,
                '&::placeholder': { color: 'text.disabled' },
              }}
            />
            {allAOs.length > 1 && (
              <Box component="select" value={filterAO} onChange={e => setFilterAO(e.target.value)}
                sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, color: 'text.primary', px: 1, py: 0.6, fontSize: '0.82rem', cursor: 'pointer' }}>
                <option value="">Alla AO</option>
                {allAOs.map(ao => <option key={ao} value={ao}>{ao}</option>)}
              </Box>
            )}
            {allFacils.length > 1 && (
              <Box component="select" value={filterFacil} onChange={e => setFilterFacil(e.target.value)}
                sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, color: 'text.primary', px: 1, py: 0.6, fontSize: '0.82rem', cursor: 'pointer' }}>
                <option value="">Alla fasilitatorer</option>
                {allFacils.map(f => <option key={f} value={f}>{f}</option>)}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', alignItems: 'center' }}>
              <SortIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <SortBtn field="createdAt" label="Datum" />
              <SortBtn field="title" label="Namn" />
              <SortBtn field="date" label="Dag" />
              <SortBtn field="facilitator" label="Fasilitator" />
              {allAOs.length > 1 && <SortBtn field="ao" label="AO" />}
            </Box>
          </Box>
          {(filterAO || filterFacil || filterSearch) && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography color="text.secondary" fontSize="0.75rem">{visibleSessions.length} av {sessions.length} workshopar</Typography>
              <Button size="small" onClick={() => { setFilterAO(''); setFilterFacil(''); setFilterSearch(''); }}
                sx={{ color: accent, textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                Rensa filter
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {loading && <CircularProgress sx={{ color: accent }} />}

      {!loading && sessions.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography color="text.secondary" mb={2}>Inga workshopar ännu.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/workshopn/new')}
            sx={{ color: accent, borderColor: accent, textTransform: 'none' }}>
            Skapa din första workshop
          </Button>
        </Box>
      )}

      {/* Sessionslista */}
      {visibleSessions.map(s => {
        const status = statusLabel(s.status);
        return (
          <Paper
            key={s.id}
            variant="outlined"
            sx={{
              mb: 2, borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
              '&:hover': { borderColor: accent },
              transition: 'border-color 0.15s',
            }}
            onClick={() => navigate(`/workshopn/${s.id}/edit`)}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>

              {/* Omslagsbild / video-thumbnail */}
              {s.imageUrl ? (
                <Box
                  component="img" src={s.imageUrl} alt=""
                  sx={{ width: 90, flexShrink: 0, objectFit: 'cover', borderRight: '1px solid', borderColor: 'divider' }}
                />
              ) : s.videoUrl ? (
                <Box
                  component="video" src={s.videoUrl} preload="metadata" muted playsInline
                  sx={{ width: 90, flexShrink: 0, objectFit: 'cover', borderRight: '1px solid', borderColor: 'divider' }}
                />
              ) : null}

              {/* Sessionskod-kolumn */}
              <Box sx={{
                width: 110, flexShrink: 0, bgcolor: accent + '12',
                borderRight: '1px solid', borderColor: 'divider',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                py: 2, px: 1, gap: 0.5,
              }}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.4rem', letterSpacing: 3, color: accent, lineHeight: 1 }}>
                  {s.sessionCode}
                </Typography>
                {s.departmentName && (
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
                    {s.departmentName}
                  </Typography>
                )}
              </Box>

              {/* Huvudinnehåll */}
              <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Titel med accent-N-behandling om "WorkshopN" ingår */}
                    <Typography fontWeight={700} fontSize="1rem" color={accent} noWrap>
                      {s.title}
                    </Typography>
                    <Typography color="text.secondary" fontSize="0.8rem" mt={0.3}>
                      {s.date && new Date(s.date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
                      {' · '}{s.config?.groups?.length || 0} grupper
                      {' · '}{s.config?.questions?.length || 0} frågor
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                      <Chip label={status.label} size="small"
                        sx={{ bgcolor: status.color + '22', color: status.color, fontWeight: 600, height: 18, fontSize: '0.68rem' }} />
                      {s.createdByName && (
                        <Typography color="text.secondary" fontSize="0.75rem">👤 {s.createdByName}</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Knappar */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <Tooltip title="Visa QR-kod">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={e => { e.stopPropagation(); setQrSession(s); }}>
                        <QrCodeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={copied === s.sessionCode ? 'Kopierat!' : 'Kopiera deltagarlink'}>
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={e => copyCode(e, s.sessionCode)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Presentationsläge">
                      <IconButton size="small" sx={{ color: '#4ade80' }}
                        onClick={e => { e.stopPropagation(); navigate(`/workshopn/${s.id}/present`); }}>
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Skriv ut hybridblad">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={e => { e.stopPropagation(); navigate(`/workshopn/${s.id}/print`); }}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={duplicated === s.id ? 'Kopierad!' : 'Kopiera workshop'}>
                      <IconButton size="small" sx={{ color: duplicated === s.id ? '#4ade80' : 'text.secondary' }}
                        onClick={e => handleDuplicate(e, s)}>
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Enkät">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={e => { e.stopPropagation(); navigate(`/workshopn/${s.id}/enkät`); }}>
                        <PollIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={`Foton (${(s.photos || []).length})`}>
                      <IconButton size="small"
                        sx={{ color: (s.photos || []).length > 0 ? accent : 'text.secondary' }}
                        onClick={e => { e.stopPropagation(); setPhotoDialog({ id: s.id, title: s.title, photos: s.photos || [] }); }}>
                        <CameraAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bjud in via QR">
                      <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                        onClick={e => { e.stopPropagation(); setInviteDialog({ id: s.id, title: s.title }); }}>
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Redigera">
                      <IconButton size="small" sx={{ color: 'text.secondary' }}
                        onClick={e => { e.stopPropagation(); navigate(`/workshopn/${s.id}/edit`); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ta bort">
                      <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}
                        onClick={e => handleDelete(e, s.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        );
      })}

      {/* QR-dialog */}
      {qrSession && (
        <QRDialog
          open={!!qrSession}
          onClose={() => setQrSession(null)}
          url={`${window.location.origin}/workshop/${qrSession.sessionCode}`}
          title={qrSession.title}
        />
      )}

      {/* Invite QR dialog */}
      {inviteDialog && (
        <InviteQRDialog
          open={!!inviteDialog}
          onClose={() => setInviteDialog(null)}
          workshopId={inviteDialog.id}
          workshopTitle={inviteDialog.title}
        />
      )}

      {/* Photo dialog */}
      <Dialog open={!!photoDialog} onClose={() => setPhotoDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700} sx={{ pb: 0 }}>
          Foton – {photoDialog?.title}
        </DialogTitle>
        <Divider sx={{ mt: 1.5 }} />
        <DialogContent sx={{ pt: 2 }}>
          {(photoDialog?.photos || []).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <WorkshopPhotoCarousel photos={photoDialog.photos} height={220} autoPlay={false} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {(photoDialog?.photos || []).map(p => (
                  <Box key={p.id} sx={{ position: 'relative', width: 72, height: 72 }}>
                    <Box component="img" src={p.url} alt={p.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1.5 }} />
                    <IconButton size="small"
                      onClick={() => handlePhotoDelete(photoDialog.id, p)}
                      sx={{
                        position: 'absolute', top: -6, right: -6,
                        bgcolor: '#ef4444', color: '#fff', width: 20, height: 20,
                        '&:hover': { bgcolor: '#dc2626' },
                      }}>
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {(photoDialog?.photos || []).length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
              Inga foton uppladdade ännu
            </Typography>
          )}
          {photoUploading && <LinearProgress sx={{ borderRadius: 1, my: 1, '& .MuiLinearProgress-bar': { bgcolor: accent } }} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button component="label" variant="contained" disabled={photoUploading}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}>
            Ladda upp foton
            <input type="file" hidden multiple accept="image/*"
              onChange={e => { if (e.target.files?.length) handlePhotoUpload(photoDialog.id, Array.from(e.target.files)); e.target.value = ''; }} />
          </Button>
          <Button onClick={() => setPhotoDialog(null)} sx={{ textTransform: 'none' }}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
